import { useCallback, useMemo } from 'react';

import { FacetItem } from '@api/catalog';

import { mergeBrandsResponses, mergeFacetResponses } from '@scripts/helpers/facets';

import { BrandItem, ExtendedFacetItem, FilterValue, FormValues } from '../../../types';
import { getFieldName, isNumberArray } from '../../../utils';
import { processFormValuesToFilters, processPriceFilter, transformPopupIsPreOrderToIsPreOrder } from '../utils';

interface UseFiltersProcessingParams {
    values: FormValues;
    fieldPrefix: string;
    priceRange: { min: number; max: number } | undefined;
    baseFilters: FormValues | null;
    isMobile: boolean;
    rememberedPrice: number[] | undefined;
    isOpen: boolean;
    currentFiltersData: { facet_props?: ExtendedFacetItem[]; brands?: BrandItem[] } | null | undefined;
    previousFiltersData: { facet_props?: ExtendedFacetItem[]; brands?: BrandItem[] } | null | undefined;
    baseFiltersData: { facet_props?: ExtendedFacetItem[]; brands?: BrandItem[] } | null | undefined;
    lastChangedBlockCode: string | undefined;
    lastChangedBlockHistory: string[];
    originalBrands: FacetItem[];
}

export const useFiltersProcessing = ({
    values,
    fieldPrefix,
    priceRange,
    baseFilters,
    isMobile,
    rememberedPrice,
    isOpen,
    currentFiltersData,
    previousFiltersData,
    baseFiltersData,
    lastChangedBlockCode,
    lastChangedBlockHistory,
    originalBrands,
}: UseFiltersProcessingParams) => {
    const priceFieldName = getFieldName('price', fieldPrefix);

    const prepareFiltersForRequest = useCallback(
        (formValues: FormValues, rememberedPriceToUse: number[] | undefined) => {
            const valuesForProcessing = transformPopupIsPreOrderToIsPreOrder(formValues, fieldPrefix);

            processPriceFilter(valuesForProcessing, fieldPrefix, priceRange, rememberedPriceToUse);

            return processFormValuesToFilters(valuesForProcessing, fieldPrefix, baseFilters || undefined);
        },
        [fieldPrefix, priceRange, baseFilters]
    );

    const priceValueForRequest = useMemo(() => {
        const priceValue = values[priceFieldName];

        return priceValue !== undefined && isNumberArray(priceValue) ? priceValue : undefined;
    }, [values, priceFieldName]);

    const preparedFormFilters = useMemo(() => {
        const rememberedPriceToUse = isMobile ? priceValueForRequest : rememberedPrice;

        return prepareFiltersForRequest(values, rememberedPriceToUse);
    }, [values, rememberedPrice, priceValueForRequest, isMobile, prepareFiltersForRequest]);

    const shouldMakeRequest =
        Boolean(isOpen) && preparedFormFilters !== null && Object.keys(preparedFormFilters).length > 0;

    const getFilterKeysFromHistoryKey = useCallback((historyKey: string): string[] => {
        if (historyKey === 'popup_isPreOrder') {
            return ['availability_status'];
        }

        if (historyKey === 'price') {
            return ['price_gte', 'price_lte'];
        }

        return [historyKey];
    }, []);

    const preparedFiltersWithoutLast = useMemo(() => {
        if (!preparedFormFilters) {
            return null;
        }

        if (!lastChangedBlockCode) {
            return preparedFormFilters;
        }

        const keysToExclude = getFilterKeysFromHistoryKey(lastChangedBlockCode);
        const keysToExcludeSet = new Set(keysToExclude);

        const result: Record<string, FilterValue> = {};

        Object.keys(preparedFormFilters).forEach(key => {
            if (!keysToExcludeSet.has(key)) {
                result[key] = preparedFormFilters[key];
            }
        });

        const hasFilters = Object.keys(result).length > 0;

        return hasFilters ? result : null;
    }, [preparedFormFilters, lastChangedBlockCode, getFilterKeysFromHistoryKey]);

    const mergedFacetProps = useMemo(() => {
        if (!currentFiltersData && !previousFiltersData && !baseFiltersData) return null;

        const baseFacets = baseFiltersData?.facet_props || [];
        const currentFacets = currentFiltersData?.facet_props || [];
        const previousFacets = previousFiltersData?.facet_props || [];
        const blockCode = lastChangedBlockCode ? String(lastChangedBlockCode) : undefined;

        return mergeFacetResponses(baseFacets, currentFacets, previousFacets, blockCode);
    }, [baseFiltersData, currentFiltersData, previousFiltersData, lastChangedBlockCode]);

    const mergedBrands = useMemo(() => {
        if (!currentFiltersData && !previousFiltersData && !baseFiltersData) return null;

        const baseBrands = baseFiltersData?.brands || [];
        const currentBrands = currentFiltersData?.brands || [];
        const previousBrands = previousFiltersData?.brands || [];
        const blockCode = lastChangedBlockCode ? String(lastChangedBlockCode) : undefined;

        return mergeBrandsResponses(baseBrands, currentBrands, previousBrands, blockCode);
    }, [baseFiltersData, currentFiltersData, previousFiltersData, lastChangedBlockCode]);

    const preparedFiltersFromRequest = useMemo(() => {
        if (!currentFiltersData) return null;

        const brandsToUse: ExtendedFacetItem[] =
            mergedBrands && mergedBrands.length > 0
                ? [
                      {
                          code: 'brand_id',
                          name: 'Бренд',
                          values: mergedBrands.map((item: BrandItem) => ({
                              ...item,
                              code: item.id,
                          })),
                      },
                  ]
                : originalBrands.length > 0
                  ? originalBrands
                  : [];

        const facetProps = mergedFacetProps || currentFiltersData.facet_props || [];

        const preparedFilters = [...brandsToUse, ...facetProps];

        return preparedFilters.length > 0 ? preparedFilters : null;
    }, [currentFiltersData, mergedFacetProps, mergedBrands, originalBrands]);

    return {
        preparedFormFilters,
        preparedFiltersWithoutLast,
        shouldMakeRequest,
        preparedFiltersFromRequest,
        mergedFacetProps,
        mergedBrands,
    };
};
